/**
 * Signed URL Cache
 * Module-level cache for signed URLs with expiration tracking
 */

// Cache structure: Map<storageKey, { url: string, expiresAt: number }>
const cache = new Map();

/**
 * Get a signed URL from cache if valid
 * @param {string} key - Storage key (e.g., "projectId/images/file.webp")
 * @returns {string|null} - Cached URL or null if expired/missing
 */
export function getSignedURL(key) {
    const cached = cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
        return cached.url;
    }
    return null;
}

/**
 * Store signed URLs in cache with expiration
 * @param {Object} urlMap - Map of storage keys to signed URLs
 * @param {number} ttlMs - Time to live in milliseconds (default: 23 hours)
 */
export function setSignedURLs(urlMap, ttlMs = 23 * 60 * 60 * 1000) {
    const expiresAt = Date.now() + ttlMs;
    for (const [key, url] of Object.entries(urlMap)) {
        if (url) {
            cache.set(key, { url, expiresAt });
        }
    }
}

/**
 * Clear expired URLs from cache
 */
export function clearExpired() {
    const now = Date.now();
    for (const [key, cached] of cache.entries()) {
        if (cached.expiresAt <= now) {
            cache.delete(key);
        }
    }
}

/**
 * Clear all cached URLs
 */
export function clearAll() {
    cache.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
    const now = Date.now();
    let valid = 0;
    let expired = 0;
    
    for (const cached of cache.values()) {
        if (cached.expiresAt > now) {
            valid++;
        } else {
            expired++;
        }
    }
    
    return { valid, expired, total: cache.size };
}

// Automatically clear expired entries every 5 minutes
setInterval(clearExpired, 5 * 60 * 1000);
